"use strict";

/**
 * PHASE VALUE V2C.2A — shared presentation module for Value.
 *
 * Pure CommonJS module (no React/JSX/browser globals/process.env/console).
 * Houses the single implementation of the four Value-tab label builders
 * (buildValueBetMarketRowLabel, buildValueBetSelectionRowLabel,
 * buildCanonicalValueBetDisplayLabel, canonicalValueBetMarketForTranslation)
 * plus the small set of Arb/Middles-shared helpers that both this module and
 * ./analysisTranslate.js need, so neither side duplicates the logic:
 *   isMeaningfulLineArbMiddle, ARB_MIDDLE_SCOPE_LABELS,
 *   resolveArbMiddleScopeLabel, arbMiddleScopeAlreadyDisplayed,
 *   arbMiddleLineAlreadyInLabel, looksLikeRawSharpTokenString.
 *
 * This module must never require ./analysisTranslate.js — that file is the
 * UI-facing wrapper and re-exports the four builders above for existing
 * callsites (ValueBetsPage.jsx, ArbitragePage.jsx, MiddlesPage.jsx).
 *
 * This is a pure refactor (Option A): output for every existing input is
 * unchanged, including the current "[object Object]" leak on plain-object
 * `line` values and the current empty-string canonical-label fallbacks.
 */

const {
  extractEmbeddedLineFromRawMarket,
  classifySportForAnalysisTab,
  splitMarketPeriodForTranslation,
  inferImplicitFullTimeScopeArbMiddle,
  resolveMarketCanonicalKey,
  normalizeLookupKey,
  normalizeSharpMarketKeyForDisplay,
  translateMarketDisplay,
  translateSelectionDisplay,
  mapFixtureBetToLabel,
} = require("./analysisDataMaps");

const {
  splitOrdinalEsportsMapSetMarket,
  composeOrdinalEsportsMapSetMarketLabel,
} = require("./analysisMarketPresentation.shared");

const { getAnalysisTextData } = require("./analysisTranslationData.shared");

/* --------------------------------------------------------------------------
 * Shared Arb/Middles + Value helpers.
 *
 * Used both here (by the Value builders below) and by
 * ./analysisTranslate.js (by buildArbitrageMiddleMarketRowLabel,
 * mergeArbMiddleItemLineFields and buildValueBetTeamResolvedSelectionLabel,
 * which stay in analysisTranslate.js). Single implementation, exported once,
 * imported back into analysisTranslate.js rather than duplicated.
 * -------------------------------------------------------------------------- */

function isMeaningfulLineArbMiddle(line) {
  const s = line == null ? "" : String(line).trim();
  if (!s) return false;
  if (s === "—" || s === "-") return false;
  return true;
}

/** Short universal scope tokens for Arbitrage/Middles market row only (not locale-specific). */
const ARB_MIDDLE_SCOPE_LABELS = {
  fullTime: "FT",
  firstHalf: "HT",
  secondHalf: "2H",
  firstSet: "Set 1",
  secondSet: "Set 2",
  firstQuarter: "Q1",
  secondQuarter: "Q2",
  thirdQuarter: "Q3",
  fourthQuarter: "Q4",
  first10Min: "10m",
  first15Min: "15m",
  thirtyMinutes: "30m",
  sixtyMinutes: "60m"
};

function resolveArbMiddleScopeLabel(scopeKey) {
  if (!scopeKey) return "";
  return ARB_MIDDLE_SCOPE_LABELS[scopeKey] || "";
}

function arbMiddleScopeAlreadyDisplayed(baseTr, scopeLabel) {
  const m = String(baseTr || "").toLowerCase();
  const s = String(scopeLabel || "").toLowerCase().trim();
  if (!m || !s) return false;
  if (/^(ft|ht|2h|10m|15m|30m|60m|q1|q2|q3|q4)$/i.test(s)) {
    if (new RegExp(` · ${s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i").test(m.trim())) return true;
    const escaped = s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`, "i").test(m);
  }
  if (s === "set 1" || s === "set 2") {
    return (s === "set 1" ? /\bset\s*1\b/i : /\bset\s*2\b/i).test(m);
  }
  if (m.includes(s)) return true;
  if (/\b(1st\s*half|first\s*half|2nd\s*half|second\s*half|full\s*time|fulltime|toàn\s*trận|cả\s*trận|hiệp\s*1|hiệp\s*2)\b/i.test(m)) {
    return true;
  }
  if (/\b(ft|ht|1h|2h|q1|q2|q3|q4)\b/i.test(m)) return true;
  return false;
}

function arbMiddleLineAlreadyInLabel(label, line) {
  const rawSel = String(label ?? "").trim();
  const rawLine = String(line ?? "").trim();
  if (!rawSel || !rawLine) return false;
  if (!isMeaningfulLineArbMiddle(rawLine)) return false;
  const segs = rawSel.split(/\s*·\s*/).map((p) => p.trim());
  if (segs.some((seg) => seg === rawLine)) return true;
  const lineCore = rawLine.replace(/\s+/g, "");
  if (!lineCore) return false;
  const escaped = lineCore.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[^0-9.,])${escaped}($|[^0-9])`, "i");
  return re.test(rawSel);
}

function looksLikeRawSharpTokenString(s) {
  const t = String(s ?? "").trim();
  if (!t) return false;
  if (/[a-z0-9]_[a-z0-9_]+/i.test(t)) return true;
  if (/^(home|away)_(over|under)$/i.test(t)) return true;
  return false;
}

/* --------------------------------------------------------------------------
 * Private helpers used only by the Value label builders below (not shared
 * with any function still in ./analysisTranslate.js).
 * -------------------------------------------------------------------------- */

function readValueBetLineForSemanticLabel(bet) {
  const v =
    bet?.line ??
    bet?.market?.line ??
    bet?.selection?.line ??
    bet?.point ??
    bet?.handicap ??
    bet?.total ??
    "";
  return v == null ? "" : String(v).trim();
}

function extractNumericLineCandidateForSemantic(text) {
  const s = String(text ?? "").trim();
  if (!s) return "";
  const m = s.match(/[-+]?\d+(?:\.\d+)?/);
  return m ? m[0] : "";
}

function valueBetLineIsMeaningful(line) {
  const s = String(line ?? "").trim();
  if (!s) return false;
  if (s === "—" || s === "-") return false;
  return true;
}

function readSharpMarketTypeSnake(bet) {
  // Trust official Sharp structured fields first; never fall back to free-text `market`.
  return String(bet?.market_type ?? bet?.marketType ?? bet?.market_type ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
}

function readValueBetTeamsForSemanticLabel(bet) {
  const readTeamName = (v) => {
    const s = String(v ?? "").trim();
    return s ? s : "";
  };

  const parseTeamsFromMatch = (matchStr) => {
    const m = String(matchStr ?? "").trim();
    if (!m) return { home: "", away: "" };
    const lower = m.toLowerCase();
    const vsIdx = lower.indexOf(" vs ");
    if (vsIdx >= 0) {
      const left = m.slice(0, vsIdx).trim();
      const right = m.slice(vsIdx + 4).trim();
      if (left && right) return { home: left, away: right };
    }
    const vIdx = lower.indexOf(" v ");
    if (vIdx >= 0) {
      const left = m.slice(0, vIdx).trim();
      const right = m.slice(vIdx + 3).trim();
      if (left && right) return { home: left, away: right };
    }
    return { home: "", away: "" };
  };

  const fromMatch = parseTeamsFromMatch(bet?.match ?? bet?.game ?? "");
  const home =
    readTeamName(bet?.home_team ?? bet?.homeTeam ?? bet?.fixture?.teams?.home?.name ?? bet?.fixture?.teams?.home) ||
    fromMatch.home ||
    "";
  const away =
    readTeamName(bet?.away_team ?? bet?.awayTeam ?? bet?.fixture?.teams?.away?.name ?? bet?.fixture?.teams?.away) ||
    fromMatch.away ||
    "";
  return { home, away };
}

function parseValueBetStructuredSideAndOu(selectionTypeRaw, selectionRaw) {
  const normTok = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s./-]+/g, "_")
      .replace(/__+/g, "_")
      .replace(/^_+|_+$/g, "");

  const st = normTok(selectionTypeRaw);
  const sel = normTok(selectionRaw);

  const tryOne = (src) => {
    if (!src) return { side: "", ou: "" };
    if (src === "home_over") return { side: "home", ou: "over" };
    if (src === "home_under") return { side: "home", ou: "under" };
    if (src === "away_over") return { side: "away", ou: "over" };
    if (src === "away_under") return { side: "away", ou: "under" };
    if (/^(home|h)_(over|o)$/.test(src)) return { side: "home", ou: "over" };
    if (/^(home|h)_(under|u)$/.test(src)) return { side: "home", ou: "under" };
    if (/^(away|a)_(over|o)$/.test(src)) return { side: "away", ou: "over" };
    if (/^(away|a)_(under|u)$/.test(src)) return { side: "away", ou: "under" };
    if (src === "over" || src === "o") return { side: "", ou: "over" };
    if (src === "under" || src === "u") return { side: "", ou: "under" };
    if (src === "home" || src === "h") return { side: "home", ou: "" };
    if (src === "away" || src === "a") return { side: "away", ou: "" };
    if (src === "draw" || src === "x") return { side: "draw", ou: "" };
    return { side: "", ou: "" };
  };

  const primary = tryOne(st);
  if (primary.side || primary.ou) return primary;
  return tryOne(sel);
}

/** Exact team name or `"{team} {line}"` prefix — conservative, no fuzzy substring match. */
function inferValueBetSpreadSideAndSelectionRemainder(selRaw, home, away) {
  const s = String(selRaw ?? "").trim();
  if (!s) return { side: "", rest: "" };
  const low = s.toLowerCase();
  const h = String(home ?? "").trim();
  const a = String(away ?? "").trim();
  if (h && low === h.toLowerCase()) return { side: "home", rest: "" };
  if (a && low === a.toLowerCase()) return { side: "away", rest: "" };
  if (h) {
    const hp = `${h.toLowerCase()} `;
    if (low.startsWith(hp)) return { side: "home", rest: s.slice(h.length).trim() };
  }
  if (a) {
    const ap = `${a.toLowerCase()} `;
    if (low.startsWith(ap)) return { side: "away", rest: s.slice(a.length).trim() };
  }
  return { side: "", rest: "" };
}

function joinValueBetSemanticParts(parts) {
  const out = [];
  for (let i = 0; i < parts.length; i += 1) {
    const x = String(parts[i] ?? "").trim();
    if (!x) continue;
    const last = out[out.length - 1];
    if (last && last.toLowerCase() === x.toLowerCase()) continue;
    out.push(x);
  }
  return out.join(" ").replace(/\s+/g, " ").trim();
}

function stripRepeatedLeadingTeamName(label, teamName) {
  const s = String(label ?? "").trim();
  const t = String(teamName ?? "").trim();
  if (!s || !t) return s;
  const esc = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
  const re = new RegExp(`^(?:${esc}\\s+)+`, "i");
  return s.replace(re, `${t} `).replace(/\s+/g, " ").trim();
}

const VALUE_SEMANTIC_TEAM_TOTAL_MT = new Set([
  "team_total_goals",
  "team_total_runs",
  "team_total_points",
  "1st_half_team_total_goals",
  "2nd_half_team_total_goals",
  "1st_half_team_total_runs",
  "2nd_half_team_total_runs",
  "1st_half_team_total_points",
  "2nd_half_team_total_points",
  "first_half_team_total_goals",
  "second_half_team_total_goals",
  "first_half_team_total_runs",
  "second_half_team_total_runs",
  "first_half_team_total_points",
  "second_half_team_total_points"
]);

const VALUE_SEMANTIC_GAME_TOTAL_MT = new Set([
  "total_goals",
  "total_runs",
  "total_points",
  "1st_half_total_goals",
  "2nd_half_total_goals",
  "1st_half_total_runs",
  "2nd_half_total_runs",
  "1st_half_total_points",
  "2nd_half_total_points",
  "first_half_total_goals",
  "second_half_total_goals",
  "first_half_total_runs",
  "second_half_total_runs",
  "first_half_total_points",
  "second_half_total_points"
]);

const VALUE_SEMANTIC_SPREAD_MT = new Set([
  "point_spread",
  "run_line",
  "puck_line",
  "asian_handicap",
  "european_handicap"
]);

const VALUE_SEMANTIC_MONEYLINE_MT = new Set([
  "moneyline",
  "moneyline_3_way",
  "moneyline_3way",
  "1x2",
  "draw_no_bet",
  "match_winner",
  "winner",
  "h2h"
]);

const VALUE_SEMANTIC_ODD_EVEN_MT = new Set(["odd_even", "home_odd_even", "away_odd_even"]);

function ouWord(lang, which) {
  if (which === "over") {
    return translateSelectionDisplay("over", lang) || mapFixtureBetToLabel("over", lang) || "Over";
  }
  if (which === "under") {
    return translateSelectionDisplay("under", lang) || mapFixtureBetToLabel("under", lang) || "Under";
  }
  return "";
}

function oddEvenWord(lang, which) {
  if (which === "odd") {
    return translateSelectionDisplay("odd", lang) || mapFixtureBetToLabel("odd", lang) || "Odd";
  }
  if (which === "even") {
    return translateSelectionDisplay("even", lang) || mapFixtureBetToLabel("even", lang) || "Even";
  }
  return "";
}

function spreadFamilyLabel(lang, mtSnake) {
  if (mtSnake === "run_line") {
    return translateMarketDisplay("run line", lang) || "Run Line";
  }
  if (mtSnake === "puck_line") {
    return translateMarketDisplay("puck line", lang) || "Puck Line";
  }
  if (mtSnake === "european_handicap") {
    return translateMarketDisplay("european handicap", lang) || "European Handicap";
  }
  if (mtSnake === "asian_handicap") {
    return translateMarketDisplay("asian handicap", lang) || "Asian Handicap";
  }
  return translateMarketDisplay("handicap", lang) || translateMarketDisplay("point spread", lang) || "Handicap";
}

/**
 * Local equivalent of analysisTranslate.js's `getMarketTerm(lang, key)` — calls
 * `getAnalysisTextData` directly (same pattern already used by
 * ./analysisMarketPresentation.shared.js for `periodScopes`). Deliberately does
 * NOT import getAnalysisText/getMarketTerm from analysisTranslate.js (that would
 * create a require cycle back into the UI wrapper). The only behavioral
 * difference versus the analysisTranslate.js wrapper is that the dev-only
 * missing-translation-key console warning does not fire from here — the
 * returned string (including the fallback / "[missing:...]" shape) is identical.
 */
function getMarketTermForValueLabel(lang, key) {
  return getAnalysisTextData(lang, "marketTerms", key, "");
}

/**
 * Market row label for Value tab (normal sports): translated market family · period/scope only.
 * Uses Value semantics to resolve canonical market family, then appends FT/HT/etc. when applicable.
 * Does not append the numeric line (the selection row carries the line).
 */
function buildValueBetMarketRowLabel(item, lang) {
  if (!item || typeof item !== "object") return "";

  const rawMarketType = String(item?.market_type ?? item?.marketType ?? "").trim();
  const rawDisplayMarket = String(item?.display_market ?? item?.displayMarket ?? "").trim();
  const rawMarket = String(item?.market ?? "").trim();

  const bestRaw = rawMarketType || rawDisplayMarket || rawMarket;
  if (!bestRaw) return "";

  const normMtSnake = String(rawMarketType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");

  const isScopeTokenOnly = (s) => {
    const t = String(s ?? "").trim();
    if (!t) return false;
    if (/^(ft|ht|2h|q[1-4]|10m|15m|30m|60m)$/i.test(t)) return true;
    if (/^set\s*[12]$/i.test(t)) return true;
    return false;
  };

  const humanizeSharpMarketType = (mt) => {
    const raw = String(mt ?? "").trim();
    if (!raw) return "";
    const parts = raw
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace(/__+/g, "_")
      .replace(/^_+|_+$/g, "")
      .split("_")
      .filter(Boolean);
    if (!parts.length) return raw;
    const cap = (w) => (w ? w[0].toUpperCase() + w.slice(1) : "");
    const small = new Set(["of", "and", "or", "to", "no"]);
    const out = parts.map((w, idx) => {
      if (!w) return w;
      if (w === "1x2") return "1X2";
      if (w === "btts") return "BTTS";
      if (w === "h2h") return "H2H";
      if (idx > 0 && small.has(w)) return w;
      return cap(w);
    });
    return out.join(" ").replace(/\s+/g, " ").trim();
  };

  const MARKET_TYPE_FAMILY_ALIASES = {
    run_line: "run line",
    point_spread: "point spread",
    total_runs: "total runs",
    moneyline: "moneyline",
    player_points: "player points",
  };

  const marketFamilySeed =
    (normMtSnake && MARKET_TYPE_FAMILY_ALIASES[normMtSnake]) ||
    (rawMarketType ? normalizeSharpMarketKeyForDisplay(rawMarketType) : "") ||
    rawDisplayMarket ||
    rawMarket ||
    "";

  const { remainder: rawNoLine } = extractEmbeddedLineFromRawMarket(marketFamilySeed);

  const sportTab = classifySportForAnalysisTab(item?.sport ?? "", {
    league: item?.league,
    match: item?.match ?? item?.matchName ?? item?.eventName,
  });

  const scopeSplit = splitMarketPeriodForTranslation(rawNoLine);
  let scopeKey = scopeSplit.scopeKey;
  const baseRaw = String(scopeSplit.baseRaw || "").trim() || rawNoLine;

  if (!scopeKey) {
    const inferred = inferImplicitFullTimeScopeArbMiddle(baseRaw, sportTab);
    if (inferred) scopeKey = inferred;
  }

  const canonValueKey = canonicalValueBetMarketForTranslation(
    marketFamilySeed || rawDisplayMarket || rawMarket,
    rawMarketType
  );
  const canonBase =
    resolveMarketCanonicalKey(canonValueKey) ||
    resolveMarketCanonicalKey(baseRaw) ||
    resolveMarketCanonicalKey(rawNoLine) ||
    resolveMarketCanonicalKey(marketFamilySeed) ||
    "";

  let baseTr = "";
  if (canonBase) baseTr = translateMarketDisplay(canonBase, lang);
  if (!baseTr) {
    baseTr =
      translateMarketDisplay(baseRaw, lang) ||
      translateMarketDisplay(rawNoLine, lang) ||
      translateMarketDisplay(bestRaw, lang) ||
      "";
  }

  const ordinal = splitOrdinalEsportsMapSetMarket(baseRaw) || splitOrdinalEsportsMapSetMarket(rawNoLine);
  if (ordinal) {
    const composed = composeOrdinalEsportsMapSetMarketLabel(lang, ordinal);
    if (composed) baseTr = composed;
  }
  if ((!baseTr || isScopeTokenOnly(baseTr)) && rawMarketType) {
    const seed = normalizeSharpMarketKeyForDisplay(rawMarketType) || rawMarketType;
    const tr = translateMarketDisplay(seed, lang) || "";
    baseTr = tr || humanizeSharpMarketType(rawMarketType);
  }

  const stripSelectionLeakFromMarketBase = (tr) => {
    const t = String(tr ?? "").trim();
    if (!t || !rawMarketType) return t;
    if (/\bplayer_/.test(normMtSnake)) return t;
    const h = String(item?.home_team ?? item?.homeTeam ?? "").trim();
    const a = String(item?.away_team ?? item?.awayTeam ?? "").trim();
    const low = t.toLowerCase();
    const startsTeam = (name) => name && low.startsWith(String(name).toLowerCase());
    const wOver = String(
      translateSelectionDisplay("over", lang) || mapFixtureBetToLabel("over", lang) || "over"
    ).toLowerCase();
    const wUnder = String(
      translateSelectionDisplay("under", lang) || mapFixtureBetToLabel("under", lang) || "under"
    ).toLowerCase();
    const startsOu =
      low === wOver ||
      low.startsWith(`${wOver} `) ||
      low === wUnder ||
      low.startsWith(`${wUnder} `) ||
      /^over\b/i.test(t) ||
      /^under\b/i.test(t);
    if (!startsTeam(h) && !startsTeam(a) && !startsOu) return t;
    const seed =
      normalizeSharpMarketKeyForDisplay(rawMarketType) || humanizeSharpMarketType(rawMarketType);
    const rebuilt =
      translateMarketDisplay(seed, lang) ||
      translateMarketDisplay(humanizeSharpMarketType(rawMarketType), lang) ||
      humanizeSharpMarketType(rawMarketType);
    return rebuilt || t;
  };
  baseTr = stripSelectionLeakFromMarketBase(baseTr);

  if (!baseTr || isScopeTokenOnly(baseTr)) {
    if (rawMarketType) baseTr = humanizeSharpMarketType(rawMarketType);
  }
  if (!baseTr || isScopeTokenOnly(baseTr)) return "";

  const scopeLabel = scopeKey ? resolveArbMiddleScopeLabel(scopeKey) : "";
  let marketPart = baseTr;
  if (scopeLabel && !arbMiddleScopeAlreadyDisplayed(baseTr, scopeLabel)) {
    marketPart = `${baseTr} · ${scopeLabel}`;
  }

  return marketPart;
}

function buildValueBetSelectionRowLabel(item, lang) {
  if (!item || typeof item !== "object") return "";

  const ds = String(item?.display_selection ?? item?.displaySelection ?? "").trim();
  const selRaw = String(item?.selection ?? item?.pick ?? item?.outcome ?? "").trim();
  const stRaw = String(item?.selection_type ?? item?.selectionType ?? "").trim();
  const { home, away } = readValueBetTeamsForSemanticLabel(item);
  const player = String(item?.player_name ?? item?.playerName ?? "").trim();
  const mtSnake = readSharpMarketTypeSnake(item);

  let lineStr = readValueBetLineForSemanticLabel(item);
  if (!valueBetLineIsMeaningful(lineStr)) lineStr = extractNumericLineCandidateForSemantic(selRaw);
  if (!valueBetLineIsMeaningful(lineStr) && ds) lineStr = extractNumericLineCandidateForSemantic(ds);
  const hasLine = valueBetLineIsMeaningful(lineStr);

  let sideOu = parseValueBetStructuredSideAndOu(stRaw, selRaw);
  if (!sideOu.side && !sideOu.ou) sideOu = parseValueBetStructuredSideAndOu(ds, ds);

  const mtHay = String(mtSnake ?? "")
    .trim()
    .toLowerCase()
    .replace(/-+/g, "_")
    .replace(/\s+/g, "_");
  const mtLooksTotalsLike =
    /\b(total|totals|over_under|overunder)\b/.test(mtHay) ||
    /\b(team_total|team_totals)\b/.test(mtHay) ||
    /\b(team_cards|team_corners)\b/.test(mtHay) ||
    /\b(total_cards|total_corners)\b/.test(mtHay) ||
    /\b(cards_over_under|corners_over_under)\b/.test(mtHay) ||
    (!!mtHay && mtHay.includes("total"));
  const mtLooksTeamTotalsLike =
    /\b(team_total|team_totals)\b/.test(mtHay) ||
    /\b(team_cards|team_corners)\b/.test(mtHay) ||
    (!!mtHay && mtHay.includes("team_total")) ||
    (/\bteam\b/.test(mtHay) && /\b(total|totals|over_under|overunder)\b/.test(mtHay));
  const mtLooksSpreadLike =
    /\b(handicap|spread|run_line|puck_line)\b/.test(mtHay) ||
    /\b(asian_handicap|european_handicap)\b/.test(mtHay) ||
    (!!mtHay &&
      /handicap|spread|run_line|puck_line|asian_handicap|european_handicap/.test(mtHay));
  const mtLooksMoneylineLike =
    /\b(moneyline|match_winner|winner|h2h|1x2|draw_no_bet)\b/.test(mtHay) || /\bmoney_line\b/.test(mtHay);
  const mtLooksPlayerPropLike = !!mtHay && (/\bplayer_/.test(mtHay) || mtHay.includes("player_"));

  const lineBasedNonMoney =
    hasLine &&
    !mtLooksMoneylineLike &&
    (mtLooksTotalsLike ||
      mtLooksTeamTotalsLike ||
      mtLooksSpreadLike ||
      mtLooksPlayerPropLike);

  const hasLineInText = (text) => {
    if (!hasLine) return false;
    const t = String(text ?? "").trim();
    if (!t) return false;
    return arbMiddleLineAlreadyInLabel(t, lineStr);
  };

  const normSel = selRaw.toLowerCase();
  const isHome = normSel === "home";
  const isAway = normSel === "away";
  const isOver = normSel === "over";
  const isUnder = normSel === "under";

  const signedLine = (() => {
    if (!hasLine) return "";
    const s = lineStr;
    if (/^[+-]/.test(s)) return s;
    const n = Number(s);
    if (Number.isFinite(n) && n > 0) return `+${s}`;
    return s;
  })();

  const pickOuWord = (ou) => {
    if (ou === "over") {
      return translateSelectionDisplay("over", lang) || mapFixtureBetToLabel("over", lang) || "Over";
    }
    if (ou === "under") {
      return translateSelectionDisplay("under", lang) || mapFixtureBetToLabel("under", lang) || "Under";
    }
    return "";
  };

  const composeFromStructured = (so) => {
    const teamOf = (s) => (s === "home" ? home : s === "away" ? away : "");
    if (so.side === "draw") {
      return translateSelectionDisplay("draw", lang) || mapFixtureBetToLabel("draw", lang) || "Draw";
    }
    if (mtLooksMoneylineLike && (so.side === "home" || so.side === "away")) {
      const t = teamOf(so.side);
      return t || "";
    }
    if (mtLooksSpreadLike && (so.side === "home" || so.side === "away") && hasLine) {
      const t = teamOf(so.side);
      if (t) return `${t} ${signedLine}`.replace(/\s+/g, " ").trim();
    }
    if (mtLooksPlayerPropLike && player && (so.ou === "over" || so.ou === "under")) {
      const o = pickOuWord(so.ou);
      if (o && hasLine) return `${player} ${o} ${lineStr}`.replace(/\s+/g, " ").trim();
      if (o) return `${player} ${o}`.replace(/\s+/g, " ").trim();
    }
    if (
      (so.side === "home" || so.side === "away") &&
      (so.ou === "over" || so.ou === "under") &&
      hasLine
    ) {
      const t = teamOf(so.side);
      const o = pickOuWord(so.ou);
      if (t && o) return `${t} ${o} ${lineStr}`.replace(/\s+/g, " ").trim();
    }
    if ((so.ou === "over" || so.ou === "under") && hasLine && !so.side) {
      const o = pickOuWord(so.ou);
      if (o) return `${o} ${lineStr}`.replace(/\s+/g, " ").trim();
    }
    if (
      mtLooksTotalsLike &&
      !mtLooksTeamTotalsLike &&
      (so.side === "home" || so.side === "away") &&
      (so.ou === "over" || so.ou === "under") &&
      hasLine
    ) {
      const o = pickOuWord(so.ou);
      if (o) return `${o} ${lineStr}`.replace(/\s+/g, " ").trim();
    }
    if ((so.ou === "over" || so.ou === "under") && hasLine) {
      const o = pickOuWord(so.ou);
      if (o) return `${o} ${lineStr}`.replace(/\s+/g, " ").trim();
    }
    if (so.ou === "over" || so.ou === "under") {
      return pickOuWord(so.ou) || "";
    }
    return "";
  };

  let composed = composeFromStructured(sideOu);
  if (composed) return composed;

  if (ds) {
    const dsp = parseValueBetStructuredSideAndOu(ds, ds);
    composed = composeFromStructured(dsp);
    if (composed) return composed;
    if (!hasLine) {
      const mapped = mapFixtureBetToLabel(ds, lang) || ds;
      if (looksLikeRawSharpTokenString(mapped)) return "";
      return mapped;
    }
    if (hasLineInText(ds)) return ds;
    if (lineBasedNonMoney) {
      if (player && (isOver || isUnder)) {
        const ouWordResolved = isOver ? pickOuWord("over") : pickOuWord("under");
        return `${player} ${ouWordResolved} ${lineStr}`.replace(/\s+/g, " ").trim();
      }
      if (isHome && home) return `${home} ${signedLine}`.replace(/\s+/g, " ").trim();
      if (isAway && away) return `${away} ${signedLine}`.replace(/\s+/g, " ").trim();
      if (isOver || isUnder) {
        const ouWordResolved = isOver ? pickOuWord("over") : pickOuWord("under");
        return `${ouWordResolved} ${lineStr}`.replace(/\s+/g, " ").trim();
      }
      const mapped = mapFixtureBetToLabel(ds, lang) || ds;
      if (!looksLikeRawSharpTokenString(mapped) && !hasLineInText(mapped)) {
        return `${mapped} ${lineStr}`.replace(/\s+/g, " ").trim();
      }
    }
    return ds;
  }

  if (player && (isOver || isUnder) && hasLine) {
    const ouWordResolved = isOver ? pickOuWord("over") : pickOuWord("under");
    return `${player} ${ouWordResolved} ${lineStr}`.replace(/\s+/g, " ").trim();
  }

  if (isHome) {
    if (!home) return looksLikeRawSharpTokenString(selRaw) ? "" : selRaw;
    if (hasLine) return `${home} ${signedLine}`.replace(/\s+/g, " ").trim();
    return home;
  }

  if (isAway) {
    if (!away) return looksLikeRawSharpTokenString(selRaw) ? "" : selRaw;
    if (hasLine) return `${away} ${signedLine}`.replace(/\s+/g, " ").trim();
    return away;
  }

  if (isOver || isUnder) {
    const ouWordResolved = isOver ? pickOuWord("over") : pickOuWord("under");
    if (hasLine) return `${ouWordResolved} ${lineStr}`.replace(/\s+/g, " ").trim();
    return ouWordResolved;
  }

  if (selRaw) {
    const p = parseValueBetStructuredSideAndOu(stRaw, selRaw);
    composed = composeFromStructured(p);
    if (composed) return composed;
    if (hasLine && !hasLineInText(selRaw) && !looksLikeRawSharpTokenString(selRaw)) {
      return `${selRaw} ${lineStr}`.replace(/\s+/g, " ").trim();
    }
    if (looksLikeRawSharpTokenString(selRaw)) return "";
    return mapFixtureBetToLabel(selRaw, lang) || "";
  }

  if (player) return player;
  return "";
}

/**
 * Deterministic semantics-first single-line label for Value bets (uses `market_type` + `selection_type`).
 * Returns "" when semantics are incomplete or unknown so callers can fall back safely.
 *
 * @param {Record<string, unknown>} bet
 * @param {string} lang
 * @returns {string}
 */
function buildCanonicalValueBetDisplayLabel(bet, lang) {
  if (!bet || typeof bet !== "object") return "";

  const mtSnake = readSharpMarketTypeSnake(bet);
  if (!mtSnake) return "";

  const stRaw = bet?.selection_type ?? bet?.selectionType ?? "";
  const selRaw = bet?.selection ?? bet?.pick ?? bet?.outcome ?? "";
  const displaySel = String(bet?.display_selection ?? bet?.displaySelection ?? "").trim();

  const { home, away } = readValueBetTeamsForSemanticLabel(bet);
  const sideOu = parseValueBetStructuredSideAndOu(stRaw, selRaw);

  const mtHay = String(mtSnake ?? "")
    .trim()
    .toLowerCase()
    .replace(/-+/g, "_")
    .replace(/\s+/g, "_");
  const mtLooksTotalsLike =
    /\b(total|totals|over_under|overunder)\b/.test(mtHay) ||
    /\b(team_total|team_totals)\b/.test(mtHay) ||
    /\b(team_cards|team_corners)\b/.test(mtHay) ||
    /\b(total_cards|total_corners)\b/.test(mtHay) ||
    /\b(cards_over_under|corners_over_under)\b/.test(mtHay);
  const mtLooksTeamTotalsLike =
    /\b(team_total|team_totals)\b/.test(mtHay) ||
    /\b(team_cards|team_corners)\b/.test(mtHay) ||
    (/\bteam\b/.test(mtHay) && /\b(total|totals|over_under|overunder)\b/.test(mtHay));
  const mtLooksSpreadLike =
    /\b(handicap|spread|run_line|puck_line)\b/.test(mtHay) ||
    /\b(asian_handicap|european_handicap)\b/.test(mtHay);
  const mtLooksMoneylineLike =
    /\b(moneyline|match_winner|winner|h2h|1x2|draw_no_bet)\b/.test(mtHay) ||
    /\bmoney_line\b/.test(mtHay);

  let lineStr = readValueBetLineForSemanticLabel(bet);
  if (!valueBetLineIsMeaningful(lineStr)) {
    lineStr = extractNumericLineCandidateForSemantic(String(selRaw ?? ""));
  }
  if (!valueBetLineIsMeaningful(lineStr) && displaySel) {
    lineStr = extractNumericLineCandidateForSemantic(displaySel);
  }

  const normalizeLoose = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s./-]+/g, " ")
      .replace(/_+/g, " ")
      .replace(/\s+/g, " ");
  const inferLooseOuFromText = (text) => {
    const n = normalizeLoose(text);
    if (!n) return "";
    const hasOver = /\bover\b/.test(n) || /\bo\b/.test(n);
    const hasUnder = /\bunder\b/.test(n) || /\bu\b/.test(n);
    if (hasOver && !hasUnder) return "over";
    if (hasUnder && !hasOver) return "under";
    return "";
  };

  const ouW = sideOu.ou ? ouWord(lang, sideOu.ou) : "";
  const teamFor = (side) => {
    if (side === "home") return home;
    if (side === "away") return away;
    return "";
  };

  let out = "";

  if (VALUE_SEMANTIC_TEAM_TOTAL_MT.has(mtSnake) || (mtLooksTotalsLike && mtLooksTeamTotalsLike && !mtLooksSpreadLike && !mtLooksMoneylineLike)) {
    const termKey =
      /\bcorners?\b/.test(mtHay) ? "teamCorners" : /\bcards?\b/.test(mtHay) ? "teamCards" : "teamTotal";
    const tt = String(getMarketTermForValueLabel(lang, termKey) ?? "").trim();
    if (!tt) return "";

    let side = sideOu.side;
    let ou = sideOu.ou;
    if (!ou) ou = inferLooseOuFromText(`${stRaw} ${selRaw} ${displaySel}`) || "";
    if (side !== "home" && side !== "away") {
      const n = normalizeLoose(`${stRaw} ${selRaw} ${displaySel}`);
      if (/\bhome\b/.test(n)) side = "home";
      else if (/\baway\b/.test(n)) side = "away";
    }

    const team = side === "home" || side === "away" ? teamFor(side) : "";
    const ouWordResolved = ou ? ouWord(lang, ou) : "";
    const hasLine = valueBetLineIsMeaningful(lineStr);

    if (team && ouWordResolved && hasLine) {
      out = joinValueBetSemanticParts([team, tt, ouWordResolved, lineStr]);
    } else if (ouWordResolved && hasLine) {
      out = joinValueBetSemanticParts([tt, ouWordResolved, lineStr]);
    } else if (team && hasLine) {
      out = joinValueBetSemanticParts([team, tt, lineStr]);
    } else if (hasLine) {
      out = joinValueBetSemanticParts([tt, lineStr]);
    } else if (team && ouWordResolved) {
      out = joinValueBetSemanticParts([team, tt, ouWordResolved]);
    } else if (ouWordResolved) {
      out = joinValueBetSemanticParts([tt, ouWordResolved]);
    } else if (team) {
      out = joinValueBetSemanticParts([team, tt]);
    } else {
      out = tt;
    }
  } else if (VALUE_SEMANTIC_GAME_TOTAL_MT.has(mtSnake) || (mtLooksTotalsLike && !mtLooksTeamTotalsLike && !mtLooksSpreadLike && !mtLooksMoneylineLike)) {
    const termKey =
      /\bcorners?\b/.test(mtHay)
        ? /\btotal_corners\b/.test(mtHay)
          ? "totalCorners"
          : "corners"
        : /\bcards?\b/.test(mtHay)
          ? /\btotal_cards\b/.test(mtHay)
            ? "totalCards"
            : "cards"
          : "gameTotal";
    const gt = String(getMarketTermForValueLabel(lang, termKey) ?? "").trim();
    if (!gt) return "";

    // Never treat this as team total even if noisy selection hints at home/away.
    if (sideOu.side === "home" || sideOu.side === "away") {
      if (valueBetLineIsMeaningful(lineStr)) return joinValueBetSemanticParts([gt, lineStr]);
      return gt;
    }

    const inferredOu = sideOu.ou || inferLooseOuFromText(`${stRaw} ${selRaw} ${displaySel}`) || "";
    const ouWordResolved = inferredOu ? ouWord(lang, inferredOu) : "";
    const hasLine = valueBetLineIsMeaningful(lineStr);

    if (ouWordResolved && hasLine) out = joinValueBetSemanticParts([gt, ouWordResolved, lineStr]);
    else if (hasLine) out = joinValueBetSemanticParts([gt, lineStr]);
    else if (ouWordResolved) out = joinValueBetSemanticParts([gt, ouWordResolved]);
    else out = gt;
  } else if (VALUE_SEMANTIC_SPREAD_MT.has(mtSnake) || (mtLooksSpreadLike && !mtLooksMoneylineLike)) {
    let spreadSide = sideOu.side;
    let spreadLine = lineStr;
    if (spreadSide !== "home" && spreadSide !== "away") {
      const inferred = inferValueBetSpreadSideAndSelectionRemainder(selRaw, home, away);
      if (inferred.side === "home" || inferred.side === "away") spreadSide = inferred.side;
      if (!valueBetLineIsMeaningful(spreadLine) && inferred.rest) {
        spreadLine = extractNumericLineCandidateForSemantic(inferred.rest);
      }
    }
    const spreadLab = spreadFamilyLabel(lang, mtSnake);
    if (!spreadLab) return "";
    if (!valueBetLineIsMeaningful(spreadLine)) {
      spreadLine = extractNumericLineCandidateForSemantic(String(selRaw ?? ""));
    }
    if (!valueBetLineIsMeaningful(spreadLine) && displaySel) {
      spreadLine = extractNumericLineCandidateForSemantic(displaySel);
    }

    const hasLine = valueBetLineIsMeaningful(spreadLine);
    const team = spreadSide === "home" || spreadSide === "away" ? teamFor(spreadSide) : "";

    if (team && hasLine) out = joinValueBetSemanticParts([team, spreadLab, spreadLine]);
    else if (hasLine) out = joinValueBetSemanticParts([spreadLab, spreadLine]);
    else if (team) out = joinValueBetSemanticParts([team, spreadLab]);
    else out = spreadLab;
  } else if (VALUE_SEMANTIC_MONEYLINE_MT.has(mtSnake) || mtLooksMoneylineLike) {
    if (mtSnake === "draw_no_bet") {
      if (sideOu.side !== "home" && sideOu.side !== "away") return "";
      const team = teamFor(sideOu.side);
      if (!team) return "";
      const dnb = translateMarketDisplay("draw no bet", lang) || "Draw No Bet";
      out = joinValueBetSemanticParts([team, dnb]);
    } else if (sideOu.side === "draw") {
      const dr = translateSelectionDisplay("draw", lang) || mapFixtureBetToLabel("draw", lang) || "Draw";
      const mk =
        mtSnake === "1x2"
          ? translateMarketDisplay("1x2", lang) || "1X2"
          : translateMarketDisplay("moneyline 3 way", lang) ||
            translateMarketDisplay("moneyline", lang) ||
            "Moneyline";
      out = joinValueBetSemanticParts([dr, mk]);
    } else if (sideOu.side === "home" || sideOu.side === "away") {
      const team = teamFor(sideOu.side);
      if (!team) return "";
      let mk = "";
      if (mtSnake === "1x2") {
        mk = translateMarketDisplay("1x2", lang) || "1X2";
      } else if (mtSnake === "moneyline_3_way" || mtSnake === "moneyline_3way") {
        mk = translateMarketDisplay("moneyline 3 way", lang) || translateMarketDisplay("moneyline", lang) || "";
      } else {
        mk = translateMarketDisplay("moneyline", lang) || translateMarketDisplay("match winner", lang) || "";
      }
      if (!mk) return "";
      out = joinValueBetSemanticParts([team, mk]);
    } else return "";
  } else if (VALUE_SEMANTIC_ODD_EVEN_MT.has(mtSnake)) {
    const oddEvenMarket = translateMarketDisplay("odd even", lang) || "Odd / Even";
    const rawTok = String(stRaw || selRaw || "")
      .trim()
      .toLowerCase()
      .replace(/[\s._-]+/g, "_");
    let which = "";
    if (rawTok.includes("odd") && !rawTok.includes("even")) which = "odd";
    else if (rawTok.includes("even") && !rawTok.includes("odd")) which = "even";
    else {
      const sLow = String(selRaw ?? "").toLowerCase();
      if (/\bodd\b/.test(sLow) && !/\beven\b/.test(sLow)) which = "odd";
      else if (/\beven\b/.test(sLow) && !/\bodd\b/.test(sLow)) which = "even";
    }
    const ow = which ? oddEvenWord(lang, which) : "";
    if (mtSnake === "odd_even") {
      if (!ow) return "";
      out = joinValueBetSemanticParts([oddEvenMarket, ow]);
    } else {
      if (sideOu.side !== "home" && sideOu.side !== "away") return "";
      const team = teamFor(sideOu.side);
      if (!team || !ow) return "";
      out = joinValueBetSemanticParts([team, oddEvenMarket, ow]);
    }
  } else {
    return "";
  }

  if (!out) return "";

  if (home) out = stripRepeatedLeadingTeamName(out, home);
  if (away) out = stripRepeatedLeadingTeamName(out, away);

  return out;
}

/**
 * Canonical market key for Value tab (same resolver path as Arbitrage).
 * @param {string} rawMarket — human `market` string when present
 * @param {string} rawFallback — structured Sharp `market_type` when present (preferred for resolution)
 */
function canonicalValueBetMarketForTranslation(rawMarket, rawFallback) {
  const ordered = [];
  const push = (v) => {
    const s = String(v ?? "").trim();
    if (!s) return;
    if (!ordered.some((x) => x.toLowerCase() === s.toLowerCase())) ordered.push(s);
  };
  push(rawFallback);
  push(rawMarket);

  const isSelectionOnlyMarketNoise = (s) => {
    const n = normalizeLookupKey(s);
    return /^(over|under|home|away|draw|yes|no|o|u)$/.test(n);
  };

  for (let i = 0; i < ordered.length; i += 1) {
    const raw = ordered[i];
    if (ordered.length > 1 && isSelectionOnlyMarketNoise(raw)) continue;
    const displayNorm = normalizeSharpMarketKeyForDisplay(raw);
    const resolved =
      resolveMarketCanonicalKey(displayNorm) ||
      resolveMarketCanonicalKey(raw) ||
      resolveMarketCanonicalKey(normalizeLookupKey(raw));
    if (resolved) {
      const nk = normalizeLookupKey(resolved).replace(/\s+/g, " ").trim();
      if (nk) return nk;
    }
  }

  const merged =
    normalizeSharpMarketKeyForDisplay(String(rawFallback ?? "").trim()) ||
    normalizeSharpMarketKeyForDisplay(String(rawMarket ?? "").trim());
  return merged || String(rawFallback ?? "").trim() || String(rawMarket ?? "").trim() || "";
}

module.exports = {
  isMeaningfulLineArbMiddle,
  ARB_MIDDLE_SCOPE_LABELS,
  resolveArbMiddleScopeLabel,
  arbMiddleScopeAlreadyDisplayed,
  arbMiddleLineAlreadyInLabel,
  looksLikeRawSharpTokenString,
  buildValueBetMarketRowLabel,
  buildValueBetSelectionRowLabel,
  buildCanonicalValueBetDisplayLabel,
  canonicalValueBetMarketForTranslation,
};
